export const TOAST_PROGRESS_LOADING_PANEL_CLASS = "w-[min(400px,calc(100vw-24px))]";
export const TOAST_SUMMARY_PANEL_CLASS = "w-[min(500px,calc(100vw-24px))]";
export const IMPORT_TOAST_PANEL_CLASS = "!w-[min(560px,calc(100vw-40px))]";

export const IMPORT_PROGRESS_STEPS = [
  { key: "analyze", label: "Analyse et validation des fichiers" },
  { key: "save", label: "Enregistrement de l’import" },
  { key: "refresh", label: "Actualisation de l’affichage" },
];

export const CLIENT_SAVE_PROGRESS_STEPS = [
  { key: "save", label: "Enregistrement du client" },
  { key: "dispatch", label: "Préparation du Dispatch, si nécessaire" },
  { key: "refresh", label: "Actualisation de la table" },
];

export const TRUCK_SAVE_PROGRESS_STEPS = [
  { key: "save", label: "Enregistrement du camion" },
  { key: "day", label: "Préparation du Dispatch Jour" },
  { key: "evening", label: "Préparation du Dispatch Soir" },
  { key: "refresh", label: "Actualisation de la table" },
];

export const TRAILER_SAVE_PROGRESS_STEPS = [
  { key: "save", label: "Enregistrement de la remorque" },
  ...TRUCK_SAVE_PROGRESS_STEPS.slice(1),
];

// Une fiche chauffeur ne prépare aucun Dispatch : ses écarts d'heures attendent dans Mise à jour > Chauffeurs.
export const DRIVER_SAVE_PROGRESS_STEPS = [
  { key: "save", label: "Enregistrement du chauffeur" },
  { key: "dispatch", label: "Dispatchs ouverts concernés" },
  { key: "refresh", label: "Actualisation de la table" },
];

export const RAILWAY_SYNC_PROGRESS_STEPS = [
  { key: "read", label: "Lecture des données Railway" },
  { key: "preflight", label: "Vérification des protections Facturation" },
  { key: "import", label: "Synchronisation des 6 tables" },
  { key: "sollio", label: "Import des adresses variables Sollio" },
  { key: "restore", label: "Restauration du Dispatch" },
  { key: "verify", label: "Contrôle de conformité locale" },
  { key: "refresh", label: "Actualisation du Data Manager" },
];

export const TRUCK_OPTIMIZATION_PROGRESS_STEPS = [
  { key: "prepare", label: "Préparation de la demande" },
  { key: "process", label: "Recalcul et enregistrement du camion" },
  { key: "refresh", label: "Actualisation de l’affichage" },
];

export const SOURCE_CHANGES_PROGRESS_STEPS = [
  { key: "process", label: "Traitement de la sélection" },
  { key: "display", label: "Actualisation du Dispatch" },
  { key: "refresh", label: "Rafraîchissement de la liste" },
];

export function createToastProgress(steps, startedAt = Date.now()) {
  if (!Array.isArray(steps) || steps.length === 0) return null;
  return {
    startedAt,
    steps: steps.map((step, index) => ({ ...step, status: index === 0 ? "running" : "pending" })),
  };
}

// Only explicit operation boundaries advance the list. There is no timer here.
export function createToastProgressReporter(initialProgress, onChange, { now = () => performance.now(), wallNow = () => Date.now() } = {}) {
  const started = now();
  let finished = false;
  let pauseBoundary = null;
  let progress = initialProgress ? { ...initialProgress, steps: initialProgress.steps.map((step) => ({ ...step })) } : null;
  const starts = new Map((progress?.steps || []).filter((step) => step.status === "running").map((step) => [step.key, started]));
  const durations = new Map();
  const snapshot = () => progress ? { ...progress, steps: progress.steps.map((step) => ({ ...step })) } : null;
  const close = (step, status, boundary) => {
    if (!durations.has(step.key)) durations.set(step.key, starts.has(step.key) ? Math.max(0, boundary - starts.get(step.key)) : null);
    return { ...step, status, duration_ms: durations.get(step.key) };
  };
  const report = (key, status = "running") => {
    if (finished || pauseBoundary !== null) return;
    if (!progress?.steps.some((step) => step.key === key)) return;
    if (status === "running" && progress.steps.find((step) => step.key === key).status !== "pending") return;
    const boundary = now();
    const steps = progress.steps.map((step) => {
      if (step.key === key) {
        if (status === "running") {
          if (step.status !== "pending") return step;
          starts.set(key, boundary);
          return { ...step, status };
        }
        if (step.status === "failed" || step.status === "warning") return step;
        return close(step, status, boundary);
      }
      if (status === "running" && step.status === "running") return close(step, "completed", boundary);
      return step;
    });
    progress = { ...progress, steps };
    onChange?.(snapshot());
  };
  report.pause = (key) => {
    if (finished || pauseBoundary !== null || !progress?.steps.some((step) => step.key === key && step.status === "running")) return;
    pauseBoundary = now();
    progress = {
      ...progress,
      pausedAt: wallNow(),
      steps: progress.steps.map((step) => step.key === key ? { ...step, status: "paused" } : step),
    };
    onChange?.(snapshot());
  };
  report.resume = () => {
    if (finished || pauseBoundary === null) return;
    const pausedDuration = Math.max(0, now() - pauseBoundary);
    progress = {
      ...progress,
      pausedAt: null,
      pausedDurationMs: (progress.pausedDurationMs || 0) + pausedDuration,
      steps: progress.steps.map((step) => {
        if (step.status !== "paused") return step;
        if (starts.has(step.key)) starts.set(step.key, starts.get(step.key) + pausedDuration);
        return { ...step, status: "running" };
      }),
    };
    pauseBoundary = null;
    onChange?.(snapshot());
  };
  report.getSnapshot = snapshot;
  report.finish = ({ status = "completed" } = {}) => {
    if (!finished) {
      const boundary = pauseBoundary ?? now();
      if (progress) progress = { ...progress, steps: progress.steps.map((step) => (
        ["running", "paused"].includes(step.status) ? close(step, status, boundary)
          : step.status === "pending" ? close(step, "skipped", boundary) : step
      )) };
      finished = true;
    }
    return snapshot();
  };
  return report;
}

export function formatToastElapsedTime(startedAt, now) {
  const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
}

// Share the whole lifecycle, including feedback and refresh, on a double click.
export function runToastProgressOperationOnce(inFlight, key, operation) {
  const existing = inFlight.get(key);
  if (existing) return existing;
  const pending = Promise.resolve().then(operation).finally(() => inFlight.delete(key));
  inFlight.set(key, pending);
  return pending;
}

export const RAILWAY_BUSINESS_COPY_PROGRESS_STEPS = [
  { key: "preflight", label: "Vérification de la compatibilité" },
  { key: "read", label: "Lecture de l’instantané Railway" },
  { key: "documents", label: "Copie et vérification des PDF" },
  { key: "maintenance", label: "Attente des opérations locales" },
  { key: "replace", label: "Remplacement des données métier" },
  { key: "verify", label: "Vérification complète et validation" },
  { key: "refresh", label: "Actualisation de l’affichage" },
];
